// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react'

// UMD brand palette
const COLORS = {
  H: { bg: '#E21833', text: '#fff', label: 'High' },
  M: { bg: '#FFD520', text: '#1a1a1a', label: 'Medium' },
  L: { bg: '#D3D3D3', text: '#1a1a1a', label: 'Low' },
  null: { bg: '#fff', text: '#1a1a1a', label: 'Not covered' },
}



interface SourceCLOEntry {
  wording: string
  coverage: string | null
}

interface CanonicalCLO {
  id: number
  statement: string
  source_clos: Record<string, SourceCLOEntry | string>
}

interface CourseInstance {
  label: string
  analysis_id: string
  coverage: { clo_id: number; level: 'H' | 'M' | 'L' | null }[]
}

export interface CLOHeatmapProps {
  canonical_clos: CanonicalCLO[]
  course_instances: CourseInstance[]
  summary?: string
}

export function CLOHeatmap({ canonical_clos, course_instances, summary }: CLOHeatmapProps) {
  const [tooltip, setTooltip] = useState<{
    x: number
    y: number
    text: string
  } | null>(null)

  // Build quick lookup: coverageMap[instanceIdx][clo_id] = level
  const coverageMap: Record<number, Record<number, 'H' | 'M' | 'L' | null>> = {}
  course_instances.forEach((inst, idx) => {
    coverageMap[idx] = {}
    inst.coverage.forEach(c => {
      coverageMap[idx][c.clo_id] = c.level
    })
  })

  const getLevel = (instanceIdx: number, cloId: number): 'H' | 'M' | 'L' | null => {
    return coverageMap[instanceIdx]?.[cloId] ?? null
  }

  const getTooltipText = (clo: CanonicalCLO, instance: CourseInstance, instIdx: number): string => {
    const positionalKey = `INSTANCE_${instIdx}`
    const entry = clo.source_clos?.[positionalKey] ?? clo.source_clos?.[instance.label]
    if (entry) {
      const wording = typeof entry === 'object' ? entry.wording : entry
      if (wording) return `Original: "${wording}"`
    }
    return 'Not covered in this instance'
  }

  const handleMouseEnter = (e: React.MouseEvent, text: string) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect()
    setTooltip({ x: rect.left + rect.width / 2, y: rect.top - 8, text })
  }

  const handleMouseLeave = () => setTooltip(null)

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg space-y-8 clo-heatmap-print">
      <div className="clo-heatmap-header">
        <h2 className="text-2xl font-bold text-gray-900 mb-1 clo-heatmap-title">CLO Coverage Heatmap</h2>
        <p className="text-sm text-gray-500 clo-heatmap-meta">
          {canonical_clos.length} canonical CLOs · {course_instances.length} course instances
        </p>
      </div>

      {/* Heatmap grid */}
      <div className="overflow-x-auto clo-heatmap-scroller">
        <div
          className="inline-grid gap-px bg-gray-200 rounded-lg overflow-hidden clo-heatmap-grid"
          style={{
            gridTemplateColumns: `minmax(220px, 1fr) repeat(${course_instances.length}, minmax(80px, 1fr))`,
          }}
        >
          {/* Header row */}
          <div className="bg-gray-100 px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider clo-heatmap-header-cell clo-heatmap-header-cell-label">
            Learning Outcome
          </div>
          {course_instances.map((inst, i) => (
            <div
              key={i}
              className="bg-gray-100 px-3 py-3 text-xs font-semibold text-gray-700 text-center truncate clo-heatmap-header-cell clo-heatmap-col-header"
              title={inst.label}
            >
              {inst.label}
            </div>
          ))}

          {/* Data rows */}
          {canonical_clos.map(clo => (
            <>
              {/* CLO label cell */}
              <div
                key={`clo-${clo.id}`}
                className="bg-white px-4 py-3 text-sm text-gray-800 flex items-start gap-2 clo-heatmap-label-cell"
              >
                <span className="text-xs font-bold text-gray-400 mt-0.5 w-5 shrink-0">
                  {clo.id}.
                </span>
                <span className="leading-snug clo-heatmap-label-text">{clo.statement}</span>
              </div>

              {/* Coverage cells */}
              {course_instances.map((inst, instIdx) => {
                const level = getLevel(instIdx, clo.id)
                const colorCfg = COLORS[level ?? 'null']
                const tooltipText = getTooltipText(clo, inst, instIdx)

                return (
                  <div
                    key={`cell-${clo.id}-${instIdx}`}
                    className="flex items-center justify-center py-3 px-2 cursor-default select-none relative clo-heatmap-value-cell"
                    style={{ backgroundColor: colorCfg.bg }}
                    onMouseEnter={e => handleMouseEnter(e, tooltipText)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {level ? (
                      <span
                        className="text-sm font-bold clo-heatmap-value-text"
                        style={{ color: colorCfg.text }}
                      >
                        {level}
                      </span>
                    ) : (
                      /* Diagonal stripe pattern for null */
                      <svg
                        className="w-6 h-6 opacity-20"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <line x1="0" y1="0" x2="24" y2="24" stroke="#666" strokeWidth="2" />
                        <line x1="12" y1="0" x2="24" y2="12" stroke="#666" strokeWidth="2" />
                        <line x1="0" y1="12" x2="12" y2="24" stroke="#666" strokeWidth="2" />
                      </svg>
                    )}
                  </div>
                )
              })}
            </>
          ))}
        </div>
      </div>

      {/* Tooltip (portal-like fixed position) */}
      {tooltip && (
        <div
          className="fixed z-50 max-w-xs bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 clo-heatmap-legend">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Legend:</span>
        {(['H', 'M', 'L', 'null'] as const).map(key => {
          const cfg = COLORS[key]
          return (
            <div key={key} className="flex items-center gap-1.5 clo-heatmap-legend-item">
              <div
                className="w-6 h-6 rounded flex items-center justify-center border border-gray-200 clo-heatmap-legend-swatch"
                style={{ backgroundColor: cfg.bg }}
              >
                {key !== 'null' && (
                  <span className="text-xs font-bold" style={{ color: cfg.text }}>{key}</span>
                )}
              </div>
              <span className="text-xs text-gray-600">{cfg.label}</span>
            </div>
          )
        })}
      </div>

      {/* Summary */}
      {summary && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 clo-heatmap-summary">
          <h3 className="text-base font-semibold text-gray-800 mb-2">Summary</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{summary}</p>
        </div>
      )}
    </div>
  )
}
